import { defineCollection } from "@rebasepro/common";
import prospectsCollection from "./prospects.js";
import mailboxesCollection from "./mailboxes.js";

/**
 * One drafted cold email, and the record of what happened to it.
 *
 * A human is the approval gate. The agent may create rows and move them as far
 * as `needs_review`; it may not move one to `approved`, and the send endpoint
 * refuses anything that is not `approved`. So the transition that turns a draft
 * into a message a stranger receives is a person — approving the card, or
 * pressing Send in the email view after reading it, which approves and sends in
 * one confirmed step (`frontend/src/views/OutreachEmailView.tsx`). There is no
 * schedule, no batch job and no code path that performs it unattended.
 *
 * `step` exists because a first touch and a third follow-up are different
 * writing jobs, not the same text resent. Follow-ups are only ever drafted off
 * a PREVIOUS email that a human actually sent, so an unattended run cannot
 * manufacture a cadence on its own.
 */
/** A value as a trimmed string, or "" — display roles are text. */
const str = (value: unknown): string => (typeof value === "string" ? value.trim() : value == null ? "" : String(value));

const emailsCollection = defineCollection({
    name: "Emails",
    singularName: "Email",
    slug: "emails",
    table: "emails",
    description: "Drafted outreach. Nothing leaves this app unless a human approves the individual card.",
    history: true,
    properties: {
        id: { name: "ID", type: "string", isId: "uuid", admin: { readOnly: true } },

        prospect: {
            name: "Person",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => prospectsCollection,
                relationName: "prospect",
                localKey: "prospect_id",
                onDelete: "cascade"
            }
        },

        mailbox: {
            name: "From",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => mailboxesCollection,
                relationName: "mailbox",
                localKey: "mailbox_id",
                onDelete: "set null"
            },
            description: "The mailbox it goes out from. Empty means the one marked for cold outreach."
        },

        to_email: {
            name: "To",
            type: "string",
            validation: { required: true },
            description: "Snapshotted at draft time, so editing the prospect later cannot silently redirect an approved email"
        },
        subject: { name: "Subject", type: "string", validation: { required: true } },
        body: {
            name: "Body",
            type: "string",
            validation: { required: true },
            admin: { multiline: true, markdown: false }
        },

        step: {
            name: "Step",
            type: "number",
            defaultValue: 1,
            validation: { min: 1, max: 3 },
            description: "1 = first touch, 2 = follow-up, 3 = final"
        },
        purpose: {
            name: "Purpose",
            type: "string",
            defaultValue: "intro",
            enum: [
                { id: "intro", label: "First touch", color: "blue" },
                { id: "follow_up", label: "Follow-up", color: "purple" },
                { id: "breakup", label: "Final note", color: "gray" }
            ]
        },

        status: {
            name: "Status",
            type: "string",
            validation: { required: true },
            defaultValue: "needs_review",
            enum: [
                { id: "needs_review", label: "Needs review", color: "yellow" },
                { id: "approved", label: "Approved to send", color: "blue" },
                { id: "sent", label: "Sent", color: "green" },
                { id: "replied", label: "They replied", color: "teal" },
                { id: "rejected", label: "Rejected", color: "gray" },
                { id: "failed", label: "Send failed", color: "red" }
            ]
        },

        personalization_notes: {
            name: "What it used",
            type: "string",
            admin: { multiline: true, readOnly: true },
            description: "The model states which facts it used and which it could not verify. Read this before approving."
        },
        degraded: {
            name: "Templated",
            type: "boolean",
            defaultValue: false,
            admin: { readOnly: true },
            description: "True when no model was available and this is interpolated template text. Never approve one blind."
        },

        sent_at: { name: "Sent at", type: "date", admin: { readOnly: true } },
        approved_by: { name: "Approved by", type: "string", admin: { readOnly: true } },
        error: { name: "Error", type: "string", admin: { readOnly: true, multiline: true } },

        created_at: { name: "Created", type: "date", autoValue: "on_create", admin: { readOnly: true } },
        __order: { name: "Order", type: "string", admin: { disabled: true, hideFromCollection: true } }
    },
    admin: {
        icon: "Mail",
        group: "Outreach",
        // Named, not imported: this file is also loaded by the backend for its
        // schema, and importing a React action here would pull the admin bundle
        // in with it. The names are resolved against the actions registered on
        // `<Rebase entityActions={…}>` — that provider, not `<RebaseCMS>`, is
        // the one `resolveEntityAction` reads, which is why registering them
        // only on the latter left the menu showing just the built-ins.
        entityActions: ["approve_email", "reject_email", "send_email"],
        defaultViewMode: "list",
        enabledViews: ["list", "table", "kanban"],
        kanban: { columnProperty: "status" },
        orderProperty: "__order",
        /**
         * A review queue, so the row shows what is being approved: who it goes
         * to and how it opens. Reading the first line is most of the review —
         * the rest is whether that line is true of that person.
         *
         * `degraded` earns a chip because it means the model refused or the
         * budget was spent and this is the templated fallback. Sending one is a
         * decision; not noticing you sent one is an accident.
         */
        display: {
            title: "subject",
            subtitle: ({ entity }) => {
                const body = str(entity.values.body).split("\n").find(line => line.trim());
                return [str(entity.values.to_email), body].filter(Boolean).join(" — ");
            },
            status: "status",
            date: "created_at",
            tags: ({ entity }) => [
                str(entity.values.purpose),
                entity.values.step ? `step ${entity.values.step}` : "",
                entity.values.degraded ? "templated" : ""
            ].filter(Boolean)
        },
        sort: ["created_at", "desc"],
        propertiesOrder: [
            "subject",
            "status",
            "mailbox",
            "to_email",
            "body",
            "personalization_notes",
            "degraded",
            "step",
            "purpose",
            "prospect",
            "sent_at",
            "approved_by",
            "error",
            "created_at",
            "__order"
        ],
        filterPresets: [
            { label: "Waiting on me", filterValues: { status: ["==", "needs_review"] } },
            { label: "Approved, not yet sent", filterValues: { status: ["==", "approved"] } },
            { label: "Templated (no model)", filterValues: { degraded: ["==", true] } }
        ]
    }
});

export default emailsCollection;
